import axios from "axios";
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";
import { API_URL, PATHS, APP_ROUTES } from "./constants";

/* ======================================
 * AUTH USER TYPE
 * ====================================== */
export type AuthUser = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string;
  email: string | null;
  roleId: number;
  roleName: string | null;
  roleIds?: number[];
  roleNames?: string[];
  userCode?: string | null;
  modules?: string[];
  supplierId?: number | null;
  supplierCompanyName?: string | null;
};

/** A role the user can choose at login (the portal chooser). */
export type AvailableRole = { id: number; name: string };

/* ======================================
 * SESSION CACHE (no tokens; cookie-based)
 * ====================================== */
const AUTH_KEY = import.meta.env.VITE_AUTH_STORAGE_KEY || "tt.auth.v1";

export type PersistedAuth = {
  accessToken: null; // no tokens in cookie mode
  user: AuthUser | null;
  expiresAt?: number | null;
  refreshToken?: null;
};

function emptyAuth(): PersistedAuth {
  return { accessToken: null, user: null, expiresAt: null, refreshToken: null };
}

export function getAuth(): PersistedAuth {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    return raw
      ? ({ accessToken: null, ...JSON.parse(raw) } as PersistedAuth)
      : emptyAuth();
  } catch {
    return emptyAuth();
  }
}

export function setAuth(next: Partial<PersistedAuth>) {
  const merged: PersistedAuth = {
    ...getAuth(),
    ...next,
    accessToken: null,
    refreshToken: null,
  };
  localStorage.setItem(AUTH_KEY, JSON.stringify(merged));
  window.dispatchEvent(new CustomEvent("auth:updated", { detail: merged }));
}

export function clearAuth() {
  localStorage.removeItem(AUTH_KEY);
  window.dispatchEvent(new Event("auth:cleared"));
}

/* ======================================
 * LOGOUT HANDLING
 * ====================================== */
let onLogoutCb: (() => void) | null = null;

export function onLoggedOut(cb: () => void) {
  onLogoutCb = cb;
}

function hardLogout() {
  clearAuth();
  onLogoutCb?.();
  if (!onLogoutCb) window.location.assign(APP_ROUTES.login);
}

/* ======================================
 * AXIOS INSTANCE
 * ====================================== */
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true, // send HttpOnly cookies
  timeout: 30_000,
  // headers: { "Content-Type": "application/json" },
});

/* ======================================
 * REQUEST INTERCEPTOR
 * ====================================== */
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Nothing to attach — cookies are handled automatically.
  return config;
});

/* ======================================
 * REFRESH SESSION (cookie-based)
 * ====================================== */
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

async function refreshSession(): Promise<void> {
  // Backend should reissue the auth cookie
  await axios.post(API_URL + PATHS.auth.refresh, {}, { withCredentials: true });
}

function ensureRefreshOnce(): Promise<void> {
  if (isRefreshing && refreshPromise) return refreshPromise;
  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      await refreshSession();
    } finally {
      isRefreshing = false;
      refreshPromise = null; // allow retry next time
    }
  })() as Promise<void>;

  return refreshPromise;
}

/* ======================================
 * RESPONSE INTERCEPTOR (Handle 401)
 * ====================================== */
api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & {
      _retry?: boolean;
    }) | undefined;
    const status = error.response?.status;

    if (!original || status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      await ensureRefreshOnce();
      return api(original);
    } catch (refreshErr) {
      // Do NOT hardLogout globally.
      // Let the guard handle redirects based on /auth/me.
      // Only force logout if the failing request IS /auth/me.
      const url = String(original.url || "");
      if (url.includes(PATHS.auth.me)) {
        hardLogout();
      }
      return Promise.reject(refreshErr);
    }
  }
);

/* ======================================
 * GENERIC REQUEST WRAPPER (nicer errors + debug)
 * ====================================== */
// function extractApiMessage(err: any): string {
//   const data = err?.response?.data;
//   const msg =
//     data?.message ||
//     data?.error ||
//     (typeof data === "string" ? data : null) ||
//     err?.message ||
//     "Something went wrong.";
//   return String(msg);
// }

type ApiErrorPayload = {
  ok?: boolean;
  message?: string;
  error?: string;
  errors?: {
    formErrors?: string[];
    fieldErrors?: Record<string, string[] | string>;
  };
};

function extractApiMessage(err: any): string {
  const data = err?.response?.data as ApiErrorPayload | undefined;

  // 1) If we have a zod-style errors block, build a detailed message
  if (data?.errors) {
    const pieces: string[] = [];

    // formErrors: ["Something went wrong", ...]
    if (Array.isArray(data.errors.formErrors) && data.errors.formErrors.length) {
      pieces.push(...data.errors.formErrors);
    }

    // fieldErrors: { cities: ["City is required"], ... }
    if (data.errors.fieldErrors && typeof data.errors.fieldErrors === "object") {
      for (const [field, msgs] of Object.entries(data.errors.fieldErrors)) {
        if (!msgs) continue;
        const list = Array.isArray(msgs) ? msgs : [msgs];
        if (!list.length) continue;

        pieces.push(`${field}: ${list.join(", ")}`);
      }
    }

    if (pieces.length) {
      // Prefix with main message if present
      if (data.message) {
        return `${data.message} – ${pieces.join(" | ")}`;
      }
      return pieces.join(" | ");
    }
  }

  // 2) Fallbacks: message → error → raw string → generic
  const msg =
    data?.message ||
    data?.error ||
    (typeof data === "string" ? data : null) ||
    err?.message ||
    "Something went wrong.";

  return String(msg);
}


export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const started =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  try {
    const res = await api.request<T>(config);

    if (process.env.NODE_ENV !== "production") {
      const ended =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      // eslint-disable-next-line no-console
      console.debug(
        `[API ${String(config.method || "GET").toUpperCase()}] ${
          config.url
        } → ${res.status} in ${Math.round(
          (ended as number) - (started as number)
        )}ms`
      );
    }

    return res.data as T;
  } catch (err: any) {
    const status = err?.response?.status;
    const msg = extractApiMessage(err);

    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error(
        `[API ERROR ${String(config.method || "GET").toUpperCase()}] ${
          config.url
        } → ${status ?? "—"}: ${msg}`,
        err?.response?.data ?? err
      );
    }

    const e = new Error(msg) as Error & { status?: number; payload?: unknown };
    e.status = status;
    e.payload = err?.response?.data;
    throw e;
  }
}

export default api;

/* ======================================
 * LOGIN / LOGOUT HELPERS
 * ====================================== */
export type LoginDto = {
  username?: string;
  email?: string;
  password: string;
};

export type LoginResult =
  | { step: "otp"; otpRequired: true }
  | {
      step: "select-role";
      selectionToken: string;
      availableRoles: AvailableRole[];
    }
  | { step: "done"; user: AuthUser | null; expiresAt: number | null };

export type LoginResponseShapes =
  | { ok?: boolean; data?: { user?: AuthUser; expiresAt?: number } }
  | { user?: AuthUser; expiresAt?: number }
  | any;

export async function login(dto: LoginDto): Promise<LoginResult> {
  const res = await api.post(PATHS.auth.login, dto);
  const payload = res.data;

  // Backend shape (per your controller):
  // - Step 1 (OTP required):
  //   { ok: true, message: "OTP sent to your email.", data: { otpRequired: true } }
  // - Step 2 (login success):
  //   { ok: true, message: "Login successful.", data: { user: {...}, expiresAt?: number } }

  const data = payload?.data ?? payload;

  // STEP 1 → OTP required
  if (data && data.otpRequired) {
    // Do NOT set auth, no user yet
    return {
      step: "otp",
      otpRequired: true,
    };
  }

  // STEP 1b → multiple roles: user must pick a portal/role before a session is
  // issued. No auth set yet; the caller renders the chooser and calls selectRole().
  if (data && data.roleSelectionRequired) {
    return {
      step: "select-role",
      selectionToken: String(data.selectionToken ?? ""),
      availableRoles: Array.isArray(data.availableRoles)
        ? (data.availableRoles as AvailableRole[])
        : [],
    };
  }

  // STEP 2 → final login, user present
  const rawUser =
    (payload?.data && payload.data.user) ??
    payload?.user ??
    null;

  const user: AuthUser | null = rawUser ?? null;

  const expiresAt =
    (payload?.data && typeof payload.data.expiresAt === "number"
      ? payload.data.expiresAt
      : undefined) ??
    (typeof payload?.expiresAt === "number" ? payload.expiresAt : undefined) ??
    null;

  // Persist user snapshot (cookie-mode: no tokens here)
  setAuth({ user, expiresAt, accessToken: null, refreshToken: null });

  return {
    step: "done",
    user,
    expiresAt,
  };
}

/**
 * Step 2 of the multi-role login: exchange the short-lived selectionToken and
 * the chosen roleId for a real session (cookie set by the backend).
 */
export async function selectRole(input: {
  selectionToken: string;
  roleId: number;
}): Promise<Extract<LoginResult, { step: "done" }>> {
  const res = await api.post(PATHS.auth.selectRole, input);
  const payload = res.data;

  const user: AuthUser | null =
    (payload?.data && payload.data.user) ?? payload?.user ?? null;

  const expiresAt =
    (payload?.data && typeof payload.data.expiresAt === "number"
      ? payload.data.expiresAt
      : undefined) ??
    (typeof payload?.expiresAt === "number" ? payload.expiresAt : undefined) ??
    null;

  setAuth({ user, expiresAt, accessToken: null, refreshToken: null });

  return { step: "done", user, expiresAt };
}

export async function logout(): Promise<void> {
  try {
    await api.post(PATHS.auth.logout); // backend clears cookie
  } finally {
    hardLogout();
  }
}
